<template>
  <BaseTemplate app-name="RIS System" page-title="">
    <template #content>
      <div class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200">
        <!-- Main Content -->
        <div class="flex-1 flex overflow-hidden">
          <!-- Content Area -->
          <div class="flex-1 overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div class="flex flex-wrap justify-between items-center gap-4">
                <div class="flex items-center gap-3">
                  <h2 class="text-xl font-medium" :style="{ color: 'var(--clinic-font-color)' }">{{ currentView === 'list' ? 'Gerenciamento de Monitores' : 'Cadastro de Monitor' }}</h2>
                  <button
                    v-if="currentView === 'form'"
                    class="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    @click="goToList"
                    aria-label="Voltar para lista"
                  >
                    <ArrowLeftIcon class="w-5 h-5" />
                  </button>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    v-if="currentView === 'list'"
                    class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                    @click="openNewMonitorForm"
                  >
                    <PlusIcon class="w-4 h-4" />
                    <span>Novo Monitor</span>
                  </button>
                  <button
                    v-if="currentView === 'form'"
                    class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                    @click="saveMonitor"
                    :disabled="!isFormValid"
                  >
                    <SaveIcon class="w-4 h-4" />
                    <span>{{ editMode ? 'Atualizar' : 'Salvar' }} Monitor</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Monitor List View -->
            <div v-if="currentView === 'list'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700 p-4">
                <!-- Search and Filter -->
                <div class="mb-4 flex flex-wrap gap-4">
                  <div class="flex-1 min-w-[240px]">
                    <div class="relative">
                      <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Buscar monitores..."
                        class="w-full px-3 py-2 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                  <div class="w-full md:w-auto flex gap-2">
                    <div class="relative">
                      <select
                        v-model="filterStatus"
                        class="h-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                      >
                        <option value="">Todos os status</option>
                        <option value="true">Ativo</option>
                        <option value="false">Inativo</option>
                      </select>
                      <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                    <button
                      @click="resetFilters"
                      class="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <RefreshCwIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <!-- Monitors Table -->
                <div class="overflow-x-auto">
                  <table class="w-full border-collapse">
                    <thead>
                      <tr class="bg-gray-50 dark:bg-gray-900/50 text-left">
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">ID</th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Nome</th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Status</th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="filteredMonitors.length === 0">
                        <td colspan="8" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                          Nenhum monitor encontrado
                        </td>
                      </tr>
                      <tr v-for="monitor in filteredMonitors" :key="monitor.id"
                        class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <td class="px-4 py-3 text-sm">{{ monitor.id }}</td>
                        <td class="px-4 py-3">
                          <div class="flex items-center gap-3">
                            <div class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 bg-indigo-500">
                              {{ getMonitorInitials(monitor.name) }}
                            </div>
                            <div class="text-sm font-medium">{{ monitor.name }}</div>
                          </div>
                        </td>

  
                        <td class="px-4 py-3 text-sm">
                          <span :class="['px-2 py-1 rounded text-xs font-medium', 
                            monitor.active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200']">
                            {{ monitor.active ? 'Ativo' : 'Inativo' }}
                          </span>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex gap-2">
                            <button
                              @click="editMonitor(monitor)"
                              class="p-1 text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400"
                              title="Editar"
                            >
                              <EditIcon class="w-4 h-4" />
                            </button>
                            <button
                              @click="confirmDeleteMonitor(monitor)"
                              class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                              title="Excluir"
                            >
                              <TrashIcon class="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div class="mt-4 flex justify-between items-center">
                  <div class="text-sm text-gray-500 dark:text-gray-400">
                    Mostrando página {{ page }} de {{ totalPages }} (total {{ total }} monitores)
                  </div>
                  <div class="flex items-center gap-4">
                    <label for="pageSize" class="text-sm text-gray-600 dark:text-gray-300">
                      Itens por página:
                    </label>
                    <select
                      id="pageSize"
                      v-model="pageSize"
                      @change="fetchMonitors(1)"
                      class="px-2 py-1 border rounded bg-white dark:bg-gray-800 text-sm"
                    >
                      <option :value="10">10</option>
                      <option :value="20">20</option>
                      <option :value="30">30</option>
                    </select>
                    <button
                      class="px-3 py-1 border rounded-md"
                      @click="goPrev"
                      :disabled="page <= 1"
                    >
                      Anterior
                    </button>
                    <button
                      class="px-3 py-1 border rounded-md"
                      @click="goNext"
                      :disabled="page >= totalPages"
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Monitor Form View -->
            <div v-if="currentView === 'form'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700">
                <!-- Form Content -->
                <div class="p-6">
                  <div class="space-y-4">
                    <!-- Nome do Monitor -->
                    <div>
                      <label for="name" class="block text-sm font-medium mb-1">Nome do Monitor*</label>
                      <input
                        id="name"
                        type="text"
                        v-model="currentMonitor.name"
                        required
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                    <!-- Login do Monitor -->
                    <div>
                      <label for="login" class="block text-sm font-medium mb-1">Login*</label>
                      <input
                        id="login"
                        type="text"
                        v-model="currentMonitor.login"
                        required
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                    <!-- Email do Monitor -->
                    <div>
                      <label for="email" class="block text-sm font-medium mb-1">Email*</label>
                      <input
                        id="email"
                        type="email"
                        v-model="currentMonitor.email"
                        required
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                    <!-- Senha do Monitor -->
                    <div>
                      <label for="password" class="block text-sm font-medium mb-1">Senha*</label>
                      <input
                        id="password"
                        type="password"
                        v-model="currentMonitor.password"
                        :required="!editMode"
                        :placeholder="editMode ? 'Deixe em branco para manter a senha atual' : ''"
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                    <!-- Status -->
                    <div class="flex items-center pt-2">
                      <input
                        id="active"
                        type="checkbox"
                        v-model="currentMonitor.active"
                        class="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-600 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label for="active" class="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Monitor Ativo
                      </label>
                    </div>
                  </div>

                  <!-- Form Actions -->
                  <div class="mt-8 flex justify-between items-center">
                    <div class="flex gap-2"></div>
                    <div class="flex gap-3">
                      <button
                        class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                        @click="goToList"
                      >
                        Cancelar
                      </button>
                      <button
                        class="clinic-btn text-white rounded-md transition-colors shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                        @click="saveMonitor"
                        :disabled="!isFormValid"
                      >
                        {{ editMode ? 'Atualizar' : 'Salvar' }} Monitor
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Success Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showSuccessModal"
              @click.self="closeSuccessModal"
            >
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn">
                <div class="p-6 text-center">
                  <div class="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto mb-4">
                    <CheckIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2">{{ editMode ? 'Monitor Atualizado' : 'Monitor Cadastrado' }}</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6">
                    {{ editMode ? 'O monitor foi atualizado com sucesso.' : 'O monitor foi cadastrado com sucesso no sistema.' }}
                  </p>
                  <div class="flex justify-center gap-3">
                    <button
                      class="clinic-btn text-white rounded-md transition-colors shadow-sm"
                      @click="closeSuccessModal"
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Error Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showErrorModal"
              @click.self="showErrorModal = false"
            >
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div class="p-6 text-center">
                  <div class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 mx-auto mb-4 flex items-center justify-center">
                    <AlertTriangleIcon class="w-8 h-8 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 class="text-xl font-medium mb-2 text-red-600">Erro ao salvar</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6">{{ errorMessage }}</p>
                  <button
                    class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md shadow-sm"
                    @click="showErrorModal = false"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showDeleteModal"
              @click.self="showDeleteModal = false"
            >
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn">
                <div class="p-6">
                  <div class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mx-auto mb-4">
                    <AlertTriangleIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2 text-center">Confirmar Exclusão</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 text-center">
                    Tem certeza que deseja excluir o monitor <span class="font-medium">{{ monitorToDelete?.name }}</span>?
                    <br />Esta ação não pode ser desfeita.
                  </p>
                  <div class="flex justify-end gap-3">
                    <button
                      class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                      @click="showDeleteModal = false"
                    >
                      Cancelar
                    </button>
                    <button
                      class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors shadow-sm"
                      @click="deleteMonitor"
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import BaseTemplate from '../templates/BaseTemplate.vue'
import apiClient from '@/services/authService'
import {
  Plus, ChevronDown, Check, Search, Edit, Trash,
  Save, ArrowLeft, RefreshCw, AlertTriangle
} from 'lucide-vue-next'

// Icons
const PlusIcon = Plus
const ChevronDownIcon = ChevronDown
const CheckIcon = Check
const SearchIcon = Search
const EditIcon = Edit
const TrashIcon = Trash
const SaveIcon = Save
const ArrowLeftIcon = ArrowLeft
const RefreshCwIcon = RefreshCw
const AlertTriangleIcon = AlertTriangle

// Estados principais
const currentView = ref('list')
const showSuccessModal = ref(false)
const showErrorModal = ref(false)
const showDeleteModal = ref(false)
const editMode = ref(false)
const monitorToDelete = ref(null)
const errorMessage = ref('')

const searchQuery = ref('')
const filterStatus = ref('')

const total = ref(0)
const page = ref(1)
const pageSize = ref(10)

const totalPages = computed(() => Math.ceil(total.value / pageSize.value))

// Modelo vazio para monitor
const emptyMonitor = {
  id: null,
  name: '',
  login: '',
  email: 'monitor@monitor.com',
  password: '',
  active: true
}

// Estados reativos
const monitors = ref([])
const currentMonitor = ref({ ...emptyMonitor })

// Buscar dados
async function fetchMonitors(p = 1) {
  try {
    const { data } = await apiClient.get('/api/v1/monitors/', {
      params: {
        page: p,
        page_size: pageSize.value,
        search: searchQuery.value || undefined,
        active: filterStatus.value || undefined,
      }
    })
    total.value = data.total
    page.value = data.page
    monitors.value = data.results || []
  } catch (e) {
    console.error('Erro ao buscar monitores:', e)
  }
}

// Salvar monitor
async function saveMonitor() {
  if (!isFormValid.value) return
  
  const payload = {
    name: currentMonitor.value.name,
    login: currentMonitor.value.login,
    email: currentMonitor.value.email,
    active: currentMonitor.value.active
  }

  // Só incluir senha se foi preenchida (para edição) ou se é um novo monitor
  if (currentMonitor.value.password.trim() || !editMode.value) {
    payload.password = currentMonitor.value.password
  }

  try {
    if (editMode.value) {
      await apiClient.put(`/api/v1/monitors/${currentMonitor.value.id}/`, payload)
    } else {
      await apiClient.post('/api/v1/monitors/', payload)
    }
    showSuccessModal.value = true
  } catch (err) {
    const detail = err.response?.data?.detail || 'Erro ao salvar monitor'
    errorMessage.value = detail
    showErrorModal.value = true
  }
}

// Editar monitor
async function editMonitor(monitor) {
  try {
    const { data } = await apiClient.get(`/api/v1/monitors/${monitor.id}/`)
    currentMonitor.value = {
      id: data.id,
      name: data.name,
      login: data.login || '',
      email: data.email || 'monitor@monitor.com',
      password: '',
      active: data.active
    }
    editMode.value = true
    currentView.value = 'form'
  } catch (err) {
    console.error('Erro ao carregar monitor:', err)
  }
}

// Excluir monitor
async function deleteMonitor() {
  if (!monitorToDelete.value) return
  try {
    await apiClient.delete(`/api/v1/monitors/${monitorToDelete.value.id}/`)
    monitors.value = monitors.value.filter(m => m.id !== monitorToDelete.value.id)
    showDeleteModal.value = false
  } catch (err) {
    console.error('Erro ao excluir monitor:', err)
  }
}

// Computed properties
const filteredMonitors = computed(() => {
  let res = [...monitors.value]
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    res = res.filter(m =>
      m.name.toLowerCase().includes(q) ||
      (m.login && m.login.toLowerCase().includes(q)) ||
      (m.email && m.email.toLowerCase().includes(q)) ||
      String(m.id).includes(q)
    )
  }
  if (filterStatus.value) {
    const isActive = filterStatus.value === 'true'
    res = res.filter(m => m.active === isActive)
  }
  return res
})

const isFormValid = computed(() => {
  const hasRequiredFields = currentMonitor.value.name.trim() &&
    currentMonitor.value.login.trim() &&
    currentMonitor.value.email.trim()
  
  // Para novos monitores, senha é obrigatória
  if (!editMode.value) {
    return hasRequiredFields && currentMonitor.value.password.trim()
  }
  
  // Para edição, senha é opcional (pode manter a atual)
  return hasRequiredFields
})

// Funções auxiliares
function getMonitorInitials(name) {
  if (!name) return ''
  return name
    .split(' ')
    .map(w => w[0]?.toUpperCase() || '')
    .join('')
    .substring(0, 2)
}

function openNewMonitorForm() {
  currentMonitor.value = { ...emptyMonitor }
  editMode.value = false
  currentView.value = 'form'
}

function goToList() {
  currentView.value = 'list'
}

function confirmDeleteMonitor(m) {
  monitorToDelete.value = m
  showDeleteModal.value = true
}

function closeSuccessModal() {
  showSuccessModal.value = false
  currentView.value = 'list'
  fetchMonitors(page.value)
}

function resetFilters() {
  searchQuery.value = ''
  filterStatus.value = ''
  fetchMonitors(1)
}

function goPrev() {
  if (page.value > 1) {
    fetchMonitors(page.value - 1)
  }
}

function goNext() {
  if (page.value < totalPages.value) {
    fetchMonitors(page.value + 1)
  }
}

// Watchers
watch([searchQuery, filterStatus, pageSize], () => {
  fetchMonitors(1)
})

// Lifecycle
onMounted(() => {
  fetchMonitors()
})
</script>

<style scoped>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

.clinic-btn {
  background-color: var(--clinic-btn-bg);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
}

.clinic-btn:hover:not(:disabled) {
  background-color: var(--clinic-btn-bg-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.clinic-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
